import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Download, RefreshCw, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { MonthPicker } from '@/components/gst/return-parts';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import {
  IMS_CSV_HEADERS,
  downloadImsCsv,
  lastMonth,
  monthLabel,
  useIms,
  useImsMutations,
  type ImsAction,
  type ImsMatch,
  type ImsRow,
  type ImsView,
} from '@/lib/gst';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useOrgLocale } from '@/lib/org-locale';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/taxes/gst/ims')({
  validateSearch: (s: Record<string, unknown>): { period?: string } =>
    typeof s['period'] === 'string' && /^\d{4}-\d{2}$/.test(s['period'])
      ? { period: s['period'] }
      : {},
  component: Ims,
});

const MATCH: Record<ImsMatch, { label: string; tone: string }> = {
  matched: { label: 'Matched', tone: 'bg-[#ECFDF3] text-[#067647]' },
  probable: { label: 'Probable match', tone: 'bg-[#EFF8FF] text-[#175CD3]' },
  mismatch: { label: 'Mismatch', tone: 'bg-[#FFFAEB] text-[#B54708]' },
  not_in_books: { label: 'Not in books', tone: 'bg-[#FEF3F2] text-[#B42318]' },
};
const ACTION: Record<ImsAction, string> = {
  accept: 'Accepted',
  reject: 'Rejected',
  pending: 'Pending',
  no_action: 'No action',
};
const CHIPS: Array<[string, string]> = [
  ['all', 'All'],
  ['matched', 'Matched'],
  ['probable', 'Probable'],
  ['mismatch', 'Mismatch'],
  ['not_in_books', 'Not in books'],
  ['open', 'No action yet'],
  ['reject', 'Rejected'],
];

/** GST of all heads in paise (bigint: amounts never go through a float). */
const tax = (r: { igst: string; cgst: string; sgst: string; cess: string }) =>
  [r.igst, r.cgst, r.sgst, r.cess].reduce((a, x) => a + (toMinor(x) ?? 0n), 0n);
const rupees = (paise: bigint) => formatMinor(paise);

/**
 * Screen T12 (document 06): Taxes › GST › IMS and GSTR-2B. Imports the portal's GSTR-2B JSON (or
 * the CSV template), matches each supplier document to the bills in the books, and keeps the
 * accept / reject / pending actions as a worksheet: no GSP is connected, so the actions are taken
 * on the portal's IMS dashboard by hand.
 */
function Ims() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const { taxPack } = useOrgLocale();
  const period = Route.useSearch().period ?? lastMonth();
  const q = useIms(period);
  const m = useImsMutations(period);
  const me = useMe();
  const staff = me.data?.isStaff ?? false;
  const file = useRef<HTMLInputElement>(null);

  const onFile = async (f: File) => {
    const content = await f.text();
    const source =
      /\.csv$/i.test(f.name) || !content.trimStart().startsWith('{') ? 'csv' : 'gstr2b';
    m.importFile.mutate(
      { source, fileName: f.name, content },
      {
        onSuccess: (v) => {
          toast(
            `${v.rows.length} supplier documents for ${monthLabel(v.period)} imported and matched.`,
          );
          if (v.period !== period)
            void navigate({ to: '/taxes/gst/ims', search: { period: v.period } });
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
      },
    );
  };

  return (
    <>
      <PageHeader
        title="IMS and GSTR-2B"
        description="Supplier invoices and notes from the GST portal, matched to your bills, with the action to take on each."
        actions={
          taxPack === 'in' && orgId ? (
            <>
              <MonthPicker
                period={period}
                onChange={(p) => void navigate({ to: '/taxes/gst/ims', search: { period: p } })}
              />
              {staff ? (
                <>
                  <input
                    ref={file}
                    type="file"
                    accept=".json,.csv,application/json,text/csv"
                    className="hidden"
                    aria-label="GSTR-2B or CSV file"
                    data-testid="ims-file"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onFile(f);
                      e.target.value = '';
                    }}
                  />
                  <Button disabled={m.importFile.isPending} onClick={() => file.current?.click()}>
                    <Upload className="size-4" />{' '}
                    {m.importFile.isPending ? 'Importing…' : 'Import GSTR-2B'}
                  </Button>
                </>
              ) : null}
            </>
          ) : undefined
        }
      />
      <p className="mb-4 text-sm">
        <Link to="/taxes/gst" className="text-primary hover:underline">
          GST
        </Link>{' '}
        › {monthLabel(period)}
      </p>
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : taxPack !== 'in' ? (
        <EmptyState title="IMS applies to India clients" />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <EmptyState title="Could not load the worksheet" hint={q.error.message} />
      ) : (
        <Body v={q.data} period={period} staff={staff} m={m} />
      )}
    </>
  );
}

function Body({
  v,
  period,
  staff,
  m,
}: {
  v: ImsView;
  period: string;
  staff: boolean;
  m: ReturnType<typeof useImsMutations>;
}) {
  const orgId = useOrgId()!;
  const { currency } = useOrgLocale();
  const [chip, setChip] = useState('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const rows = useMemo(
    () =>
      v.rows.filter((r) =>
        chip === 'all'
          ? true
          : chip === 'open'
            ? r.action === 'no_action'
            : chip === 'reject'
              ? r.action === 'reject'
              : r.matchStatus === chip,
      ),
    [v.rows, chip],
  );
  const act = (action: ImsAction, ids = [...picked]) => {
    const remark =
      action === 'reject'
        ? (window.prompt('Why reject? (kept with the action)') ?? undefined)
        : undefined;
    if (action === 'reject' && remark === undefined) return;
    m.act.mutate(
      { ids, action, ...(remark ? { remark } : {}) },
      {
        onSuccess: (r) => {
          toast(
            `${r.updated} document${r.updated === 1 ? '' : 's'} marked ${ACTION[action].toLowerCase()}.`,
          );
          setPicked(new Set());
        },
        onError: (e) => toast(e.message, { tone: 'error' }),
      },
    );
  };
  const template = () => {
    const url = URL.createObjectURL(
      new Blob([`\uFEFF${IMS_CSV_HEADERS.join(',')}\r\n`], { type: 'text/csv' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Inward-supplies-template.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!v.imports.length)
    return (
      <EmptyState
        title={`No GSTR-2B imported for ${monthLabel(period)}`}
        hint="On the GST portal open Returns › GSTR-2B for the month and download the JSON, then use Import GSTR-2B. Or fill in the CSV template from your supplier statements."
        action={
          <Button variant="outline" onClick={template}>
            <Download className="size-4" /> CSV template
          </Button>
        }
      />
    );

  const itc = v.summary.itc.itcAvailable;
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.id));
  return (
    <div className="flex flex-col gap-4">
      <div
        className="flex items-start gap-2 rounded-md border border-[#FCD34D] bg-[#FFFBEB] px-4 py-2.5 text-sm"
        data-testid="ims-worksheet-note"
      >
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[#B45309]" aria-hidden />
        <span>
          This is a worksheet. No GSP is connected, so these actions are not sent to the GST portal:
          take them on the portal&apos;s IMS dashboard before GSTR-2B is generated, using the
          downloaded list. Documents with no action are deemed accepted.
        </span>
      </div>

      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
        data-testid="ims-summary"
      >
        <Stat label="Matched" value={v.summary.matched} onClick={() => setChip('matched')} />
        <Stat label="Probable" value={v.summary.probable} onClick={() => setChip('probable')} />
        <Stat label="Mismatch" value={v.summary.mismatch} onClick={() => setChip('mismatch')} />
        <Stat
          label="Not in books"
          value={v.summary.notInBooks}
          onClick={() => setChip('not_in_books')}
        />
        <Stat label="Not on portal" value={v.summary.notOnPortal} />
        <div className="rounded-lg border bg-card px-3 py-2">
          <div className="text-xs text-muted-foreground">ITC per GSTR-2B · in books</div>
          <div className="tnum text-sm font-semibold">
            {currency} {rupees(tax(itc))}
          </div>
          <div className="tnum text-xs text-muted-foreground">
            {currency} {rupees(tax(v.summary.booksItc))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter">
          {CHIPS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={chip === k}
              onClick={() => setChip(k)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm',
                chip === k
                  ? 'border-primary bg-accent font-medium text-accent-foreground'
                  : 'bg-card text-muted-foreground hover:bg-muted',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {staff ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={m.rematch.isPending}
              onClick={() => m.rematch.mutate()}
            >
              <RefreshCw className="size-4" /> Match again
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              void downloadImsCsv(orgId, period, v.gstin ?? 'GSTIN').catch((e: Error) =>
                toast(e.message, { tone: 'error' }),
              )
            }
          >
            <Download className="size-4" /> Download worksheet
          </Button>
        </div>
      </div>

      {staff && picked.size ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm"
          data-testid="ims-bulk"
        >
          <span className="font-medium">{picked.size} selected</span>
          <Button size="sm" onClick={() => act('accept')}>
            Accept
          </Button>
          <Button size="sm" variant="outline" onClick={() => act('pending')}>
            Keep pending
          </Button>
          <Button size="sm" variant="outline" onClick={() => act('reject')}>
            Reject
          </Button>
          <Button size="sm" variant="ghost" onClick={() => act('no_action')}>
            Clear action
          </Button>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <Table data-testid="ims-table">
          <TableHeader>
            <TableRow>
              {staff ? (
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    className="size-4 accent-primary"
                    checked={allPicked}
                    onChange={() =>
                      setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.id)))
                    }
                  />
                </TableHead>
              ) : null}
              <TableHead>Supplier</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>No.</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Taxable</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead>Match</TableHead>
              <TableHead>In books</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <Row
                key={r.id}
                r={r}
                staff={staff}
                picked={picked.has(r.id)}
                onPick={() =>
                  setPicked((p) => {
                    const n = new Set(p);
                    if (n.has(r.id)) n.delete(r.id);
                    else n.add(r.id);
                    return n;
                  })
                }
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {v.notOnPortal.length ? (
        <section data-testid="ims-not-on-portal">
          <h2 className="mb-1 text-base font-semibold">Bills your suppliers have not reported</h2>
          <p className="mb-2 text-sm text-muted-foreground">
            Their input tax credit is not in GSTR-2B until the supplier files them; follow up with
            the supplier.
          </p>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>Bill no.</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Taxable</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {v.notOnPortal.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <Link
                        className="text-primary hover:underline"
                        to={`${b.kind === 'debit_note' ? '/expenses/debit-notes' : '/expenses/bills'}/${b.id}`}
                      >
                        {b.vendor}
                      </Link>
                    </TableCell>
                    <TableCell className="tnum text-xs">{b.supplierGstin}</TableCell>
                    <TableCell>{b.reference}</TableCell>
                    <TableCell>{formatDate(b.date)}</TableCell>
                    <TableCell className="tnum text-right">{formatAmount(b.taxable)}</TableCell>
                    <TableCell className="tnum text-right">{formatAmount(b.tax)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      ) : null}

      <p className="text-xs text-muted-foreground">
        Last import: {v.imports[0]!.fileName ?? v.imports[0]!.source} · {v.imports[0]!.rows}{' '}
        documents
        {v.imports[0]!.warnings.length ? ` · ${v.imports[0]!.warnings.join(' ')}` : ''}
      </p>
    </div>
  );
}

function Row({
  r,
  staff,
  picked,
  onPick,
}: {
  r: ImsRow;
  staff: boolean;
  picked: boolean;
  onPick: () => void;
}) {
  const diff = r.differences
    .map((d) => `${d.field}: portal ${d.portal}, books ${d.books}`)
    .join('\n');
  return (
    <TableRow data-testid="ims-row">
      {staff ? (
        <TableCell>
          <input
            type="checkbox"
            aria-label={`Select ${r.number}`}
            className="size-4 accent-primary"
            checked={picked}
            onChange={onPick}
          />
        </TableCell>
      ) : null}
      <TableCell>
        <div className="font-medium">{r.supplierName ?? r.supplierGstin}</div>
        <div className="tnum text-xs text-muted-foreground">{r.supplierGstin}</div>
      </TableCell>
      <TableCell className="text-xs">
        {r.docType === 'invoice'
          ? 'Invoice'
          : r.docType === 'credit_note'
            ? 'Credit note'
            : 'Debit note'}
        {r.reverseCharge ? ' · RCM' : ''}
      </TableCell>
      <TableCell>{r.number}</TableCell>
      <TableCell>{formatDate(r.date)}</TableCell>
      <TableCell className="tnum text-right">{formatAmount(r.taxable)}</TableCell>
      <TableCell className="tnum text-right">{rupees(tax(r))}</TableCell>
      <TableCell>
        <span
          className={cn('rounded-full px-2 py-0.5 text-xs font-medium', MATCH[r.matchStatus].tone)}
          title={diff || undefined}
        >
          {MATCH[r.matchStatus].label}
        </span>
        {r.differences.length ? (
          <div className="mt-0.5 text-xs text-muted-foreground">
            {r.differences.map((d) => d.field).join(', ')}
          </div>
        ) : null}
        {r.itcAvailable === false ? (
          <div className="mt-0.5 text-xs text-[#B42318]">
            ITC not available{r.itcReason ? `: ${r.itcReason}` : ''}
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        {r.matched ? (
          <Link
            className="text-primary hover:underline"
            to={`${r.matched.kind === 'debit_note' ? '/expenses/debit-notes' : '/expenses/bills'}/${r.matched.id}`}
          >
            {r.matched.reference ?? r.matched.number}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs">
        <span
          className={cn(
            r.action === 'reject' && 'text-[#B42318]',
            r.action === 'accept' && 'text-[#067647]',
          )}
        >
          {ACTION[r.action]}
        </span>
        {r.actionRemark ? <div className="text-muted-foreground">{r.actionRemark}</div> : null}
      </TableCell>
    </TableRow>
  );
}

function Stat({ label, value, onClick }: { label: string; value: number; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="rounded-lg border bg-card px-3 py-2 text-left enabled:hover:bg-muted"
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="tnum text-xl font-semibold">{value}</div>
    </button>
  );
}
